import { FormattedMessage } from 'react-intl';
import {
    PALO_QUANTITY_HEAD_SX,
    PaloOverflowAction,
    PaloPositionColumn,
    PaloPositionTable,
    paloCurrencySign,
    paloHasDescription,
} from '@synergy/components-palo';
import { ReactElement, useCallback, useMemo } from 'react';
import { useField } from 'formik';
import { useLocalizedContext } from '@synergy/components';

import { DetailPositionsTableGenericItemRow } from './detail-positions-table-generic-row';
import { DetailPositionsTableProductRow } from './detail-positions-table-product-row';
import { DetailPositionsTableTextItemRow } from './detail-positions-table-text-item-row';
import { FinancialCalculations } from '../../../../../../common/utils/financial-calculations';
import { InvoiceGenericItem } from '../../../../models/invoice-generic-item';
import {
    InvoiceItem,
    isInvoiceGenericItem,
    isInvoiceProductItem,
    isInvoiceTextItem,
} from '../../../../models/invoice-item';
import { InvoiceProductItem } from '../../../../models/invoice-product-item';
import { InvoiceSteps } from '../../../models/invoice-steps';
import { InvoiceTextItem } from '../../../../models/invoice-text-item';
import { Product } from '../../../../../../common/models/product';
import { TaxRate } from '../../../../../../common/models/tax-rate';
import { invoiceDetailStepperStateSelector } from '../../../store/invoice-detail-selectors';
import { isBlankPosition } from '../../../../../../common/components/palo/palo-first-position';
import { positionFieldNames } from '../../../models/positions-form-values';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

export type RowPosition = 'first' | 'last' | 'middle' | 'single';

export interface DetailPositionsTableProps {
    currency: string;
    taxRates: TaxRate[];
    /** Sits above the table: adding a row, adding several. */
    toolbar?: ReactElement;
    onDelete: (index: number) => void;
    onEditProductItem: (index?: number, customPosition?: InvoiceProductItem) => void;
    onEditProductProductItem: (index?: number, position?: InvoiceProductItem) => void;
    onDuplicateProductItem: (item: InvoiceProductItem, index: number) => void;
    onEditTextItem: (index: number, item: InvoiceTextItem) => void;
    onDuplicateTextItem: (item: InvoiceTextItem) => void;
    onTaxRateSelected: (index: number, taxRate: TaxRate | null) => void;
    onEditGenericItem: (index: number, item: InvoiceGenericItem) => void;
    onDuplicateGenericItem: (item: InvoiceGenericItem) => void;
    /** Puts a new position right above or below an existing one. */
    onInsert: (index: number, where: 'above' | 'below') => void;
    /** Carries a position to another place in the document. */
    onMove: (from: number, to: number) => void;
    /** Fills a row that was added blank with the article searched for in it. */
    onPickProduct: (index: number, product: Product) => void;
    /** Restricts the in-row article search to the supplier of the document. */
    supplierIds?: number[];
    /** For the message when the supplier carries nothing that matches. */
    supplierName?: string;
    /** No supplier chosen in the head yet; the search says so instead of searching. */
    supplierMissing?: boolean;
    onDeleteMany: (indices: number[]) => void;
    onDuplicateMany: (indices: number[]) => void;
    /** Why a row's account does not fit the country of the invoice address, if it does not (R-175). */
    accountHintOf?: (item: InvoiceProductItem) => string | undefined;
}

/**
 * The invoice's positions in the table every document shares.
 *
 * What the invoice has of its own are its columns and its three kinds of row —
 * an article, a surcharge, a free text that runs across the table. Picking rows
 * out, carrying one to another place, paging and the row menu belong to the
 * table itself, so the order and the other masks get them without a second
 * implementation (#2208).
 */
export const DetailPositionsTable = (props: DetailPositionsTableProps): ReactElement => {
    const {
        currency,
        taxRates,
        toolbar,
        onDelete,
        onEditProductItem,
        onEditProductProductItem,
        onDuplicateProductItem,
        onDuplicateTextItem,
        onEditTextItem,
        onTaxRateSelected,
        onEditGenericItem,
        onDuplicateGenericItem,
        onInsert,
        onMove,
        onPickProduct,
        supplierIds,
        supplierName,
        supplierMissing,
        onDeleteMany,
        onDuplicateMany,
        accountHintOf,
    } = props;
    const [items] = useField<InvoiceItem[]>(positionFieldNames.allItems);
    const readonly = useAppSelector(invoiceDetailStepperStateSelector).readonlySteps.includes(InvoiceSteps.Positions);

    const sortedValues = items.value.sort((a, b) => a.position - b.position);

    const getPosition = (index: number): RowPosition => {
        if (items.value.length === 1) {
            return 'single';
        }
        if (index === 0) {
            return 'first';
        }
        if (index === items.value.length - 1) {
            return 'last';
        }
        return 'middle';
    };

    // The currency stands in the heading, the cells carry the figure (LST-S17).
    const sign = paloCurrencySign(currency);
    const columns: PaloPositionColumn<InvoiceItem>[] = useMemo(
        () => [
            { key: 'product', type: 'name', label: <FormattedMessage id="app.table.column.product" /> },
            {
                key: 'quantity',
                type: 'quantity',
                label: (
                    <>
                        <FormattedMessage id="app.table.column.quantity" /> *
                    </>
                ),
                headSx: PALO_QUANTITY_HEAD_SX,
            },
            {
                key: 'price',
                type: 'price',
                label: (
                    <>
                        <FormattedMessage id="app.table.column.price" /> {sign} *
                    </>
                ),
            },
            { key: 'discount', type: 'percent', label: <FormattedMessage id="app.table.column.discount" /> },
            {
                key: 'counterAccount',
                type: 'classification',
                // Room for the mark of an account that does not fit the country,
                // beside the account number rather than over it (R-175), and no
                // more than that, so the table fits at 1568 pixels (R-179).
                width: 140,
                label: (
                    <>
                        <FormattedMessage id="app.common.counterAccount" /> *
                    </>
                ),
            },
            { key: 'tax', type: 'percent', label: <FormattedMessage id="app.table.column.tax" /> },
            {
                key: 'positionValue',
                type: 'amount',
                label: (
                    <>
                        <FormattedMessage id="app.table.column.position" /> {sign}
                    </>
                ),
            },
        ],
        [sign]
    );

    const { getLocalizedString } = useLocalizedContext();
    // Articles carry a description; surcharges and free texts show theirs in the row.
    const hasDescription = useCallback(
        (item: InvoiceItem) =>
            isInvoiceProductItem(item) &&
            paloHasDescription(getLocalizedString, item.shortDescription, item.longDescription),
        [getLocalizedString]
    );

    const rowActions = (item: InvoiceItem, index: number): PaloOverflowAction[] => {
        if (readonly) {
            return [];
        }

        const insert: PaloOverflowAction[] = [
            {
                label: <FormattedMessage id="app.positions.insertAbove" />,
                onClick: () => onInsert(index, 'above'),
            },
            {
                label: <FormattedMessage id="app.positions.insertBelow" />,
                onClick: () => onInsert(index, 'below'),
            },
        ];
        const remove: PaloOverflowAction = {
            label: <FormattedMessage id="app.common.delete" />,
            danger: true,
            onClick: () => onDelete(index),
        };

        if (isInvoiceProductItem(item)) {
            return [
                {
                    label: <FormattedMessage id="app.common.edit" />,
                    onClick: () =>
                        item.product ? onEditProductProductItem(index, item) : onEditProductItem(index, item),
                },
                ...insert,
                {
                    label: <FormattedMessage id="app.common.duplicate" />,
                    onClick: () => onDuplicateProductItem(item, index),
                },
                remove,
            ];
        }

        if (isInvoiceGenericItem(item)) {
            return [
                { label: <FormattedMessage id="app.common.edit" />, onClick: () => onEditGenericItem(index, item) },
                ...insert,
                {
                    label: <FormattedMessage id="app.common.duplicate" />,
                    onClick: () => onDuplicateGenericItem(item),
                },
                remove,
            ];
        }

        return [
            { label: <FormattedMessage id="app.common.edit" />, onClick: () => onEditTextItem(index, item) },
            ...insert,
            { label: <FormattedMessage id="app.common.duplicate" />, onClick: () => onDuplicateTextItem(item) },
            remove,
        ];
    };

    const saveRefused = useAppSelector(state => !!state.invoiceDetail.common.saveRefused);

    return (
        <PaloPositionTable
            items={sortedValues}
            columns={columns}
            getRowKey={(item, index) => `${item.id ?? 'new'}-${index}`}
            hasDescription={hasDescription}
            readonly={readonly}
            currency={currency}
            quantityOf={item => (isInvoiceTextItem(item) ? undefined : item.quantity)}
            amountOf={item => (isInvoiceTextItem(item) ? undefined : FinancialCalculations.getNetTotal(item))}
            onMove={onMove}
            rowActions={rowActions}
            onDeleteMany={onDeleteMany}
            onDuplicateMany={onDuplicateMany}
            toolbar={toolbar}
            // The blank first row, once a save was refused for want of a
            // complete position (#2308).
            isRowFaulty={item => saveRefused && isInvoiceProductItem(item) && isBlankPosition(item)}
            renderRow={(item, index) => {
                if (isInvoiceProductItem(item)) {
                    return (
                        <DetailPositionsTableProductRow
                            index={index}
                            item={item}
                            readonly={readonly}
                            taxRates={taxRates}
                            currency={currency}
                            position={getPosition(index)}
                            supplierIds={supplierIds}
                            supplierName={supplierName}
                            supplierMissing={supplierMissing}
                            onPickProduct={onPickProduct}
                            onTaxRateSelected={onTaxRateSelected}
                            accountHint={accountHintOf?.(item)}
                            onEdit={() =>
                                item.product ? onEditProductProductItem(index, item) : onEditProductItem(index, item)
                            }
                        />
                    );
                }

                if (isInvoiceGenericItem(item)) {
                    return (
                        <DetailPositionsTableGenericItemRow
                            index={index}
                            item={item}
                            readonly={readonly}
                            taxRates={taxRates}
                            currency={currency}
                            position={getPosition(index)}
                            onTaxRateSelected={onTaxRateSelected}
                        />
                    );
                }

                return <DetailPositionsTableTextItemRow item={item} readonly={readonly} index={index} />;
            }}
        />
    );
};
